import { Bot } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ConversationImageAttachment,
  ConversationTab as ConversationTabType,
  Crew,
  LlmCatalogModel,
  SearchContextType,
} from '../../types';
import { Card, CardContent, Input } from '../ui';
import {
  clampTextareaHeight,
  calculateDraggedTextareaHeight,
  MAX_TEXTAREA_HEIGHT_PX,
  MIN_TEXTAREA_HEIGHT_PX,
} from './conversationInputResize';
import { ConversationHeader } from './ConversationHeader';
import { ConversationMessages } from './ConversationMessages';
import { MessageInput } from './MessageInput';

interface ConversationTabProps {
  tab: ConversationTabType;
  crews: Crew[];
  message: string;
  setMessage: (message: string) => void;
  onSendMessage: (attachments?: ConversationImageAttachment[]) => void;
  onStopMessage?: () => void;
  onCrewSelect: (crewId: number) => void;
  isStreaming: boolean;
  formatTime: (date: Date | string) => string;
  useCanvas?: boolean;
  onUseCanvasChange?: (useCanvas: boolean) => void;
  onSearchContextTypeChange?: (value: SearchContextType) => void;
  onUseLiveDataChange?: (value: boolean) => void;
  onOpenKnowledgeBaseModal?: () => void;
  llmModels?: LlmCatalogModel[];
  llmDefaultModel?: string | null;
  llmLoading?: boolean;
  llmLoadError?: boolean;
  onSelectedLlmModelChange?: (value: string | null) => void;
}

export const ConversationTab: React.FC<ConversationTabProps> = ({
  tab,
  crews,
  message,
  setMessage,
  onSendMessage,
  onStopMessage,
  onCrewSelect,
  isStreaming,
  formatTime,
  useCanvas,
  onUseCanvasChange,
  onSearchContextTypeChange,
  onUseLiveDataChange,
  onOpenKnowledgeBaseModal,
  llmModels = [],
  llmDefaultModel = null,
  llmLoading = false,
  llmLoadError = false,
  onSelectedLlmModelChange,
}) => {
  const { t } = useTranslation();
  const DEFAULT_TEXTAREA_HEIGHT_PX = 112;
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCrews, setFilteredCrews] = useState<Crew[]>([]);
  const [textareaHeightPx, setTextareaHeightPx] = useState<number | undefined>(
    undefined
  );
  const [isResizing, setIsResizing] = useState(false);
  const [resizePreviewHeight, setResizePreviewHeight] = useState<
    number | undefined
  >(undefined);
  const isResizingRef = useRef(false);
  const tabRootRef = useRef<HTMLDivElement>(null);
  const resizeStartYRef = useRef(0);
  const initialHeightRef = useRef(DEFAULT_TEXTAREA_HEIGHT_PX);

  const restoreResizeSideEffects = useCallback(() => {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    isResizingRef.current = false;
  }, []);

  // Filter crews based on search query
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = crews.filter(
        crew =>
          (crew.name?.toLowerCase() || '').includes(
            searchQuery.toLowerCase()
          ) ||
          (crew.description?.toLowerCase() || '').includes(
            searchQuery.toLowerCase()
          ) ||
          (crew.categories || []).some(category =>
            (category.name || '')
              .toLowerCase()
              .includes(searchQuery.toLowerCase())
          )
      );
      setFilteredCrews(filtered);
    } else {
      setFilteredCrews(crews);
    }
  }, [crews, searchQuery]);

  const groupedCrews = useMemo(() => {
    const groups = new Map<string, { label: string; crews: Crew[] }>();
    const uncategorizedKey = '__uncategorized__';

    filteredCrews.forEach(crew => {
      const categories = crew.categories || [];
      if (categories.length === 0) {
        const existing = groups.get(uncategorizedKey);
        if (existing) {
          existing.crews.push(crew);
        } else {
          groups.set(uncategorizedKey, {
            label: t('conversations.uncategorizedCategory'),
            crews: [crew],
          });
        }
        return;
      }

      const primaryCategory = categories[0];
      const key = `category-${primaryCategory.id}`;
      const existing = groups.get(key);
      if (existing) {
        existing.crews.push(crew);
      } else {
        groups.set(key, {
          label: primaryCategory.name,
          crews: [crew],
        });
      }
    });

    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        if (a === uncategorizedKey) return 1;
        if (b === uncategorizedKey) return -1;
        return 0;
      })
      .map(([, value]) => value);
  }, [filteredCrews, t]);

  const crewName = crews.find(c => c.id === tab.selectedCrewId)?.name;

  const handleResizeStart = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }

      event.preventDefault();
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // ignore unsupported pointer capture
      }
      isResizingRef.current = true;
      resizeStartYRef.current = event.clientY;
      const textareaElement = tabRootRef.current?.querySelector('textarea');
      const measuredTextareaHeight =
        textareaElement?.getBoundingClientRect()?.height || 0;
      const startingHeight = clampTextareaHeight(
        typeof textareaHeightPx === 'number'
          ? textareaHeightPx
          : measuredTextareaHeight > 0
            ? measuredTextareaHeight
            : DEFAULT_TEXTAREA_HEIGHT_PX
      );
      initialHeightRef.current = startingHeight;
      setIsResizing(true);
      setResizePreviewHeight(startingHeight);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';

      const onPointerMove = (moveEvent: PointerEvent) => {
        if (!isResizingRef.current) {
          return;
        }

        const nextHeight = calculateDraggedTextareaHeight(
          initialHeightRef.current,
          resizeStartYRef.current,
          moveEvent.clientY
        );
        setTextareaHeightPx(nextHeight);
        setResizePreviewHeight(nextHeight);
      };

      const stopResize = () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', stopResize);
        window.removeEventListener('pointercancel', stopResize);
        setIsResizing(false);
        setResizePreviewHeight(undefined);
        restoreResizeSideEffects();
      };

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', stopResize);
      window.addEventListener('pointercancel', stopResize);
    },
    [restoreResizeSideEffects, textareaHeightPx]
  );

  useEffect(() => {
    return () => {
      restoreResizeSideEffects();
    };
  }, [restoreResizeSideEffects]);

  return (
    <div ref={tabRootRef} className="h-full flex flex-col overflow-hidden max-h-full">
      {/* Crew Info Header */}
      <ConversationHeader
        selectedCrewId={tab.selectedCrewId}
        crewName={crewName}
      />

      {/* Main Chat Area */}
      <div className="flex-1 overflow-hidden">
        <Card
          padding="none"
          className="h-full flex flex-col min-h-0 overflow-hidden max-h-full p-0 !rounded-none !border-l !border-b !border-t-0 !border-r-0 relative"
        >
          <CardContent className="flex flex-1 min-h-0 flex-col overflow-hidden p-0">
            <div className="flex flex-1 min-h-0 flex-col overflow-y-auto px-3 py-3">
              {!tab.selectedCrewId && tab.messages.length === 0 ? (
                <div className="h-full flex flex-col">
                  {/* Search Bar */}
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg
                          className="h-5 w-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                      <Input
                        type="text"
                        placeholder={t('conversations.searchTeams')}
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="pl-10 w-full"
                      />
                    </div>
                  </div>

                  {/* Crews Grid */}
                  <div className="flex-1 p-3 overflow-y-auto">
                    {groupedCrews.length > 0 ? (
                      <div className="space-y-3">
                        {groupedCrews.map(group => (
                          <div key={group.label} className="space-y-2">
                            <h3 className="text-xs uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">
                              {group.label}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-3">
                              {group.crews.map(crew => (
                                <div
                                  key={`${group.label}-${crew.id}`}
                                  className="cursor-pointer hover:shadow-lg transition-shadow duration-200"
                                  onClick={() => onCrewSelect(crew.id)}
                                >
                                  <Card className="border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 h-full">
                                    <CardContent className="p-0 h-full flex flex-col">
                                      <div className="flex items-start space-x-2 h-full">
                                        <div className="flex-shrink-0">
                                          <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                                            <Bot className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                          </div>
                                        </div>
                                        <div className="flex-1 min-w-0 flex flex-col">
                                          <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                            {crew.name}
                                          </h3>
                                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2 flex-1">
                                            {crew.description ||
                                              t('conversations.noCrewDescription')}
                                          </p>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Bot className="w-8 h-8 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            {searchQuery
                              ? t('conversations.noTeamsFound')
                              : t('conversations.noTeamsAvailable')}
                          </h3>
                          <p className="text-gray-500 dark:text-gray-400">
                            {searchQuery
                              ? t('conversations.tryAdjustSearch')
                              : t('conversations.contactAdmin')}
                          </p>
                          {searchQuery && (
                            <button
                              onClick={() => setSearchQuery('')}
                              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                            >
                              {t('conversations.clearSearch')}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <ConversationMessages
                  messages={tab.messages}
                  isStreaming={isStreaming}
                  formatTime={formatTime}
                  canvasSessionPersist={
                    useCanvas &&
                    tab.sessionId &&
                    tab.selectedCrewId != null
                      ? {
                          crewId: tab.selectedCrewId,
                          sessionId: tab.sessionId,
                        }
                      : null
                  }
                />
              )}
            </div>
          </CardContent>

          <div
            role="separator"
            aria-label="Redimensionar campo de mensagem"
            aria-orientation="horizontal"
            aria-valuemin={MIN_TEXTAREA_HEIGHT_PX}
            aria-valuemax={MAX_TEXTAREA_HEIGHT_PX}
            aria-valuenow={Math.round(
              resizePreviewHeight ?? textareaHeightPx ?? DEFAULT_TEXTAREA_HEIGHT_PX
            )}
            onPointerDown={handleResizeStart}
            className={`relative h-4 flex-shrink-0 cursor-ns-resize touch-none select-none ${
              isResizing ? 'bg-primary-50/70 dark:bg-primary-900/20' : ''
            }`}
          >
            <div
              className={`absolute inset-x-0 top-1/2 -translate-y-1/2 border-t transition-colors ${
                isResizing
                  ? 'border-primary-500 dark:border-primary-400 border-t-2'
                  : 'border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500'
              }`}
            />
            <div
              className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-1.5 w-12 rounded-full transition-colors ${
                isResizing
                  ? 'bg-primary-500 dark:bg-primary-400'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
            />
          </div>

          {/* Message Input */}
          <div className="px-3 py-3 flex-shrink-0 space-y-3">
            <MessageInput
              message={message}
              setMessage={setMessage}
              onSendMessage={onSendMessage}
              onStopMessage={onStopMessage}
              isStreaming={isStreaming}
              disabled={!tab.selectedCrewId}
              useCanvas={useCanvas}
              onUseCanvasChange={onUseCanvasChange}
              searchContextType={tab.searchContextType}
              onSearchContextTypeChange={onSearchContextTypeChange}
              useLiveData={tab.useLiveData}
              onUseLiveDataChange={onUseLiveDataChange}
              selectedKnowledgeSources={tab.selectedKnowledgeSources}
              selectedConnectorSources={tab.selectedConnectorSources}
              onOpenKnowledgeBaseModal={onOpenKnowledgeBaseModal}
              selectedLlmModel={tab.selectedLlmModel}
              llmModels={llmModels}
              llmDefaultModel={llmDefaultModel}
              llmLoading={llmLoading}
              llmLoadError={llmLoadError}
              onSelectedLlmModelChange={onSelectedLlmModelChange}
              textareaHeightPx={textareaHeightPx}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};
